/**
 * Frenzel equalisation, a dry practice progression.
 *
 * Adapted from the Frenzel-Fattah Equalizing Workshop by Eric Fattah, reproduced
 * as an appendix to the NAUI Freediver Student Manual (2021), pp.51-58.
 *
 * Why this is in a dry-training app at all: equalisation is the main limiter on
 * depth, it is free to drill on land, and dry practice tolerates far longer
 * sessions than in-water attempts. It carries no hypoxia risk when done dry.
 *
 * SCOPE NOTE — one step is deliberately altered.
 * The workshop's Step 5 teaches using the tongue as a piston and is written as a
 * prerequisite chain onto lung packing (glossopharyngeal insufflation). Packing is
 * prohibited everywhere else in this app: it has a documented injury mechanism
 * (pulmonary barotrauma, pneumothorax, arterial gas embolism, hypotensive
 * blackout), and nothing in our training progression requires it. Step 5 is
 * therefore included WITHOUT the packing prerequisite — the tongue-piston motion
 * is learnable from the snorkel drill alone — and `omitsSource` records that we
 * knowingly diverge from the printed text.
 */

export interface FrenzelStep {
  step: number;
  title: string;
  /** What this step gives you, in one line. */
  goal: string;
  instructions: string[];
  /** How to know you have it. */
  successCriteria: string;
  /** Set when our version intentionally departs from the printed workshop. */
  omitsSource?: string;
  caution?: string;
}

export const FRENZEL_STEPS: FrenzelStep[] = [
  {
    step: 1,
    title: 'Fill the mouth with air',
    goal: 'Move air into and out of the cheeks at will.',
    instructions: [
      'Fill your cheeks up like a balloon and hold the air there for a few seconds.',
      'Using your cheeks, push the air back into your lungs.',
      'Repeat until you can do it on demand.',
      'A "complete cheek fill" is cheeks filled until bursting. A "moderate cheek fill" is filled until they just start to bulge.',
    ],
    successCriteria: 'You can do a moderate cheek fill and return the air to your lungs without thinking about it.',
  },
  {
    step: 2,
    title: 'Control the epiglottis',
    goal: 'Open and close the throat independently of breathing.',
    instructions: [
      'Gargle water without swallowing: take a sip, tilt your head back, and hold the water without letting it run down your throat. The closed epiglottis is what holds it there.',
      'Practise stopping and starting an exhale at the throat rather than at the lips.',
      'Repeat until closing the epiglottis is deliberate and reliable.',
    ],
    successCriteria: 'You can hold water at the back of the throat, and stop an exhale at the throat on command.',
  },
  {
    step: 3,
    title: 'Control the soft palate',
    goal: 'Direct airflow through the nose or mouth at will.',
    instructions: [
      'Close your mouth and inhale through your nose, then exhale through your nose.',
      'Inhale through your nose again.',
      'Open your mouth.',
      'Exhale through ONLY your nose — no air should escape from your open mouth.',
    ],
    successCriteria: 'With your mouth open, you can exhale entirely through the nose.',
  },
  {
    step: 4,
    title: 'Apply the tongue block',
    goal: 'Stop airflow with the tongue alone, distinct from the epiglottis.',
    instructions: [
      'Begin exhaling through your mouth.',
      'Stop the airflow by closing your mouth — your cheeks should fill momentarily.',
      'Inhale, and begin exhaling again.',
      'This time stop the airflow by closing the epiglottis instead.',
      'Practise alternating the two so you can feel the difference.',
    ],
    successCriteria: 'You can tell, by feel, whether you stopped the air with your tongue or your throat.',
  },
  {
    step: 5,
    title: 'Use the tongue as a piston',
    goal: 'Generate equalising pressure with the tongue rather than the diaphragm.',
    instructions: [
      'Put a snorkel in your mouth and pinch your nose.',
      'Use the back of the tongue to push air, feeling the pressure build in the mouth rather than the chest.',
      'The motion is a "K" or "guh" — the back of the tongue rising against the soft palate.',
      'Repeat until the movement is small, controlled, and needs no chest effort.',
    ],
    successCriteria: 'You can build mouth pressure with the tongue alone, with the diaphragm relaxed.',
    omitsSource:
      'The printed workshop introduces this step via lung packing (glossopharyngeal '
      + 'insufflation). Packing is excluded from this app — it has a documented injury '
      + 'mechanism and no part of this training needs it. The tongue-piston motion is '
      + 'learned here from the snorkel drill only.',
    caution: 'Do not pack. If you feel chest pressure or light-headedness, stop.',
  },
  {
    step: 6,
    title: 'Uncouple the epiglottis and soft palate',
    goal: 'Hold the epiglottis closed while keeping the soft palate open.',
    instructions: [
      'The two muscles are naturally coupled — closing the epiglottis usually raises the soft palate, which blocks the nose and prevents equalisation.',
      'Practise closing the epiglottis while keeping nasal airflow available.',
      'Work on it in short sessions; this is the step that takes longest to develop.',
    ],
    successCriteria: 'You can equalise with the epiglottis closed, hearing a small click in both ears.',
    caution:
      'Never force equalisation. If your ears will not equalise, stop. Never equalise '
      + 'against a blocked nose or while congested — forcing causes barotrauma.',
  },
];

export const FRENZEL_CITATION =
  'Frenzel-Fattah Equalizing Workshop, Eric Fattah (2001, 2006), '
  + 'appendix to the NAUI Freediver Manual (2021) pp.51-58';

/** Dry Frenzel practice tolerates far longer sessions than in-water attempts. */
export const FRENZEL_PRACTICE_GUIDANCE =
  'A few minutes daily. Dry practice supports 10-15 minutes at a stretch, far more '
  + 'than you get in the water, and carries no risk when done dry. Learning it before '
  + 'an in-water course substantially improves your odds of passing.';

export const FRENZEL_SAFETY =
  'Never force equalisation, and never equalise against a blocked nose or while '
  + 'congested. Forcing causes barotrauma. Valsalva (pinch-and-blow) has a depth '
  + 'ceiling and risks round-window rupture if done forcefully.';

/**
 * Mouthfill is named here only so the term is recognisable. It is past the 20 m
 * ceiling of the NAUI Freediver course and needs an instructor, so it is not taught.
 */
export const MOUTHFILL_OUT_OF_SCOPE =
  'Mouthfill is an advanced technique for depths beyond this training scope. It '
  + 'requires in-person instruction and is not covered here.';

export const getFrenzelStep = (step: number): FrenzelStep | undefined =>
  FRENZEL_STEPS.find(s => s.step === step);

export const FRENZEL_STEP_COUNT = FRENZEL_STEPS.length;
